import { useState, type ReactNode } from "react";
import { Info, type LucideIcon } from "lucide-react";
import { Button } from "../atoms/Button/Button.js";
import { ConfirmDialog } from "../molecules/ConfirmDialog/ConfirmDialog.js";
import { RightDrawer } from "../molecules/RightDrawer/RightDrawer.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { useShellRightRailOccupied } from "../lib/shell-right-rail-context.js";
import { cn } from "../lib/utils.js";

/* Settings page pattern for the Direction B revamp (PB-5134): a main column
   with an optional meta aside, a meta card for that aside, one Save bar shared
   by every draft-based page, and the discard-changes dialog shell. Data-blind:
   copy and state arrive as props; navigation interception belongs to the host
   draft hook, never to these pieces. */

/** The aside sits beside the main column from here (Tailwind `xl`, 1280px).
 * Measured with a media query rather than drawn with `xl:` classes so the
 * meta renders exactly once: beside the content, or in the drawer — never in
 * both (PB-5216 d2, reversing PB-5178 d10's `lg`). */
export const ASIDE_BESIDE_QUERY = "(min-width: 80rem)";

/**
 * Whether the meta aside sits beside the main column: the window is at least
 * `xl` AND Shell's Player AI side rail is not taking width from main
 * (PB-5436). The rail narrows main without changing the window, so the media
 * query alone kept a 296px aside beside a squeezed page. Exported so a page's
 * own loading skeleton that mirrors the aside (AccountSettings) makes the same
 * call and never jumps sideways when the page loads.
 */
export function useSettingsAsideBeside(): boolean {
  const wideWindow = useMediaQuery(ASIDE_BESIDE_QUERY);
  const railOccupied = useShellRightRailOccupied();
  return wideWindow && !railOccupied;
}
/** The artboard's Details drawer width. */
const DETAILS_DRAWER_WIDTH = 404;

export interface SettingsPageLayoutProps {
  /** The page's form content. */
  children: ReactNode;
  /**
   * Optional aside (meta cards, pointers). Sits beside the main column from
   * `xl` (1280px) up, and at any width while the Player AI side rail is not
   * open (PB-5436); otherwise the layout does not render it at all. The page
   * then shows it through `SettingsDetailsButton`, which it passes the same
   * meta and puts in its header's actions, so a 1024px window keeps the whole
   * lane for the page and 375px / 768px never scroll sideways.
   */
  meta?: ReactNode;
  /**
   * Width of the main column's content. `reading` (the default) caps it at
   * 60rem (960px) so a form's lines stay readable on the 90rem console lane
   * (PB-5216 d1); the column itself keeps its full width, so the aside remains
   * aligned to the lane's far edge. `fluid` lets the page use every pixel.
   */
  mainWidth?: "reading" | "fluid";
  className?: string;
}

export function SettingsPageLayout({
  children,
  meta,
  mainWidth = "reading",
  className,
}: SettingsPageLayoutProps) {
  const asideBeside = useSettingsAsideBeside();
  const content = (
    <div className={mainWidth === "reading" ? "max-w-[60rem]" : undefined}>
      {children}
    </div>
  );

  /* ONE tree, not two returns. The branch only changes the root's direction
     and which SIBLING follows the main column — the main column itself, and
     therefore `children`, keeps its position and its element type on both
     sides of the breakpoint.
     
     Two returns whose first child differed in type (a `div` on one side, a
     component on the other) made React tear down and rebuild everything the
     host had passed every time the window crossed 1280px: a half-scrolled
     table jumped back to its first column, an open picker closed, and focus
     dropped to the body. What SHOULD go away on that crossing is the drawer
     and its open state, and that still does — `SettingsDetailsButton`, in
     the page header, unmounts its drawer on the same answer, which is why the
     drawer owns that state instead of an effect up here. */
  return (
    <div
      className={cn(
        "flex min-w-0",
        asideBeside ? "flex-row items-start gap-6" : "flex-col gap-6",
        className,
      )}
    >
      {/* The main column is the settings-page container page bodies query
          instead of the window (PB-5436); nearer than SettingsLayout's lane,
          so it wins where the aside takes part of the lane. */}
      <div className="@container/settings-page min-w-0 flex-1">{content}</div>
      {meta && asideBeside ? (
        /* A 296px context rail, one 24px layout step from the main column. */
        <aside
          aria-label="About this page"
          className="sticky top-4 w-74 min-w-0 shrink-0"
        >
          {meta}
        </aside>
      ) : null}
    </div>
  );
}

/**
 * The other half of `SettingsPageLayout`'s meta: a Details button that opens
 * the same meta in a drawer whenever the layout is not showing it beside the
 * main column, and nothing when it is. The page puts it in its header's
 * actions, above the header's rule, with the meta it passes the layout. It
 * used to be its own right-aligned row at the top of the main column, which
 * left a full-width band of empty space under the header (PB-5436).
 *
 * It owns the open state, so the answer changing unmounts it and the state
 * goes with it — a window narrowed again later does not reopen a drawer
 * nobody asked for. The drawer renders through a portal, so living in the
 * header costs it nothing.
 */
export function SettingsDetailsButton({ meta }: { meta: ReactNode }) {
  const asideBeside = useSettingsAsideBeside();
  // motion-check-allow: the condition is the LAYOUT (aside beside or not), not an open state — DetailsDrawer passes its own open state to RightDrawer.
  return meta && !asideBeside ? <DetailsDrawer meta={meta} /> : null;
}

function DetailsDrawer({ meta }: { meta: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Info className="h-4 w-4" aria-hidden />
        Details
      </Button>
      <RightDrawer
        open={open}
        onClose={() => setOpen(false)}
        title="Details"
        width={DETAILS_DRAWER_WIDTH}
      >
        <div className="p-4">{meta}</div>
      </RightDrawer>
    </>
  );
}

export interface SettingsMetaRow {
  readonly label: string;
  readonly value: ReactNode;
}

/** One row of the artboard's quick-links list: a label, an optional leading
 * icon and trailing accessory (a shortcut, an external-link mark), and either
 * an `href` (rendered as a link) or an `onClick` (rendered as a button). */
export interface SettingsMetaLink {
  readonly label: string;
  readonly icon?: LucideIcon;
  readonly accessory?: ReactNode;
  readonly href?: string;
  /** Open `href` in a new tab with `rel="noreferrer"`. */
  readonly external?: boolean;
  readonly onClick?: () => void;
}

export interface SettingsMetaCardProps {
  title: string;
  /** Leading icon beside the title, as the artboard's meta cards draw it. */
  icon?: LucideIcon;
  /** Short lead line under the title. */
  description?: ReactNode;
  rows?: readonly SettingsMetaRow[];
  /** Quick-link rows under the lead. */
  links?: readonly SettingsMetaLink[];
  /** Optional pointer at the bottom (a link or a small button). */
  action?: ReactNode;
  className?: string;
}

const META_LINK =
  "flex w-full items-center gap-2.5 rounded-md px-1.5 py-2 text-start text-body-small font-medium text-pb-text-secondary hover:pb-overlay-paint hover:text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/40";

/** One card in the meta aside: a title, optional lead, label/value rows, action. */
export function SettingsMetaCard({
  title,
  icon: Icon,
  description,
  rows,
  links,
  action,
  className,
}: SettingsMetaCardProps) {
  return (
    <section
      aria-label={title}
      className={cn(
        "rounded-lg border border-pb-border/80 bg-pb-card/60 p-4 text-body",
        className,
      )}
    >
      <h2 className="flex items-center gap-2 text-body-small font-semibold text-pb-text">
        {Icon ? (
          <Icon className="h-4 w-4 text-pb-text-muted" aria-hidden />
        ) : null}
        {title}
      </h2>
      {description ? (
        <p className="mt-2.5 text-body-small text-pb-text-secondary">
          {description}
        </p>
      ) : null}
      {links && links.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-0.5">
          {links.map((link) => {
            const LinkIcon = link.icon;
            const body = (
              <>
                {LinkIcon ? (
                  <LinkIcon className="h-3.75 w-3.75 shrink-0" aria-hidden />
                ) : null}
                <span className="min-w-0 flex-1 truncate">{link.label}</span>
                {link.accessory ? (
                  <span className="ms-auto shrink-0 text-pb-text-muted">
                    {link.accessory}
                  </span>
                ) : null}
              </>
            );
            return (
              <li key={link.label}>
                {link.href ? (
                  <a
                    href={link.href}
                    className={META_LINK}
                    {...(link.external
                      ? { target: "_blank", rel: "noreferrer" }
                      : {})}
                  >
                    {body}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={link.onClick}
                    className={META_LINK}
                  >
                    {body}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {rows && rows.length > 0 ? (
        <dl className="mt-3 space-y-2">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-start justify-between gap-3"
            >
              <dt className="shrink-0 text-xs text-pb-text-muted">
                {row.label}
              </dt>
              <dd className="min-w-0 text-end text-xs text-pb-text">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </section>
  );
}

export type SettingsSaveState = "clean" | "dirty" | "saving" | "saved";

interface SettingsSaveBarBaseProps {
  state: SettingsSaveState;
  onReset: () => void;
  /**
   * Hint shown while the draft is clean or just saved. Server-backed pages say
   * "Saved to your account"; mock-backed pages say "Saved for this session"
   * and pass `preview` as well (d11).
   */
  savedHint?: string;
  /** Hint shown while the draft differs from the saved state. */
  unsavedHint?: string;
  /**
   * Preview variant for a section whose backend does not exist yet: renders the
   * badge above the actions so the page never implies durable persistence.
   */
  preview?: { readonly badge: string };
  resetLabel?: string;
  saveLabel?: string;
  /**
   * Turn the reset action off. A page whose reset is a CANCEL — discard the
   * draft — has nothing to cancel while the draft is clean, and an enabled
   * button that does nothing reads as broken. A page whose reset restores
   * defaults leaves this unset: restoring defaults is meaningful at any time.
   */
  resetDisabled?: boolean;
  /**
   * Hold the save action off on top of the bar's own rule (nothing to save,
   * or a save in flight) — for a draft the page knows is invalid, so the
   * refusal is visible before the click rather than as an error after it.
   */
  saveDisabled?: boolean;
  /**
   * Extra actions rendered before Reset in the action group — non-primary
   * buttons only; Save stays the bar's only primary button. Setting this
   * makes the action group wrap (`flex-wrap`) at narrow widths.
   */
  actionsSlot?: ReactNode;
  className?: string;
}

export type SettingsSaveBarProps = SettingsSaveBarBaseProps &
  (
    | {
        /** Save renders as a regular button (today's default). */
        saveType?: "button";
        onSave: () => void;
      }
    | {
        /**
         * Save renders `type="submit"`, so Enter in a field or a click on
         * Save submits the enclosing form — the host's `onSubmit` does the
         * saving. `onSave` is not accepted in this mode: wiring both would
         * fire the save twice for one submit.
         */
        saveType: "submit";
        onSave?: never;
      }
  );

/**
 * The one Save bar for draft-based Settings pages: Reset to defaults, Save
 * changes, and a hint that says whether the draft is saved. Inline at the end
 * of the form, as in the design, not floating.
 */
export function SettingsSaveBar({
  state,
  onReset,
  onSave,
  savedHint = "All changes saved",
  unsavedHint = "Unsaved changes",
  preview,
  resetLabel = "Reset to defaults",
  saveLabel = "Save changes",
  resetDisabled = false,
  saveDisabled = false,
  actionsSlot,
  saveType = "button",
  className,
}: SettingsSaveBarProps) {
  const dirty = state === "dirty" || state === "saving";
  const hint = state === "saving" ? "Saving…" : dirty ? unsavedHint : savedHint;
  return (
    <div
      className={cn(
        "mt-6 flex flex-col gap-3 border-t border-pb-border/70 pt-4",
        className,
      )}
    >
      {preview ? (
        <span className="inline-flex w-fit items-center gap-1.5 rounded-md border border-pb-amber/40 bg-pb-amber-soft px-2 py-0.5 text-xs font-medium text-pb-amber-text">
          {preview.badge}
        </span>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* DS keep (PR 3): the save hint is live-region text beside the
            buttons, not a status indicator. */}
        <p
          role="status"
          aria-live="polite"
          className={cn(
            "text-xs",
            dirty ? "text-pb-amber-text" : "text-pb-text-muted",
          )}
        >
          {hint}
        </p>
        <div
          className={cn("flex items-center gap-2", actionsSlot && "flex-wrap")}
        >
          {actionsSlot}
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onReset}
            disabled={state === "saving" || resetDisabled}
          >
            {resetLabel}
          </Button>
          <Button
            type={saveType === "submit" ? "submit" : "button"}
            variant="primary"
            size="md"
            onClick={onSave}
            disabled={!dirty || state === "saving" || saveDisabled}
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface DiscardChangesDialogProps {
  open: boolean;
  /** The user chose to stay on the page. */
  onKeepEditing: () => void;
  /** The user chose to leave and lose the draft. */
  onDiscard: () => void;
  title?: string;
  description?: string;
}

/**
 * Shell and copy of the leave-with-unsaved-changes dialog. Whether and when it
 * opens is the host draft hook's business (router blocker, beforeunload).
 * A destructive confirm, so it is the shared ConfirmDialog (d2): the red
 * warning chip, and "Keep editing" as the ghost Cancel that takes focus.
 */
export function DiscardChangesDialog({
  open,
  onKeepEditing,
  onDiscard,
  title = "Discard unsaved changes?",
  description = "You have changes that are not saved. If you leave now they are lost.",
}: DiscardChangesDialogProps) {
  return (
    <ConfirmDialog
      open={open}
      title={title}
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      onClose={onKeepEditing}
      onConfirm={onDiscard}
    >
      {description}
    </ConfirmDialog>
  );
}
